import * as Sentry from '@sentry/react';

// Sentry PHI hardening (P2-06).
// Deny-by-default allowlist: only known-safe top-level fields forward.
// Known PHI leak vectors get dropped (extras, contexts, tags, breadcrumb
// data, query strings, request body, user fields beyond id). Exception
// messages and stack traces are preserved so production debugging stays
// useful. If a PostgREST error ever leaks user data into a message, fix
// it at the throw site (src/data/cloud.ts throwWithContext), not here.

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  tracesSampleRate: 0.1,
  sendDefaultPii: false,
  beforeSend(event) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrubbed: any = {
      event_id: event.event_id,
      timestamp: event.timestamp,
      platform: event.platform,
      level: event.level,
      environment: event.environment,
      release: event.release,
      sdk: event.sdk,
      exception: event.exception,
      request: event.request
        ? {
            url: typeof event.request.url === 'string'
              ? event.request.url.split('?')[0]
              : undefined,
            method: event.request.method,
          }
        : undefined,
      user: event.user?.id
        ? { id: String(event.user.id) }
        : undefined,
      breadcrumbs: Array.isArray(event.breadcrumbs)
        ? event.breadcrumbs.map((b) => ({
            category: b.category,
            level: b.level,
            timestamp: b.timestamp,
            type: b.type,
          }))
        : undefined,
    };
    return scrubbed;
  },
});

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import './design/globals.css';
import { initializeTheme } from './design/useTheme';
import { initializeContentLevel } from './data/useContentLevel';
import { initPurchases } from './utils/revenuecat';
// Eagerly evaluate recoveryHash so window.location.hash is captured BEFORE
// Supabase auth processes (and clears) it. ResetPasswordPage reads from this
// snapshot rather than racing onAuthStateChange. See recoveryHash.ts header.
import './auth/recoveryHash';
import App from './App';

// Apply saved theme and content level before React renders
initializeTheme();
initializeContentLevel();

// On native (Capacitor) builds, unregister any previously installed
// service worker. Vite-plugin-pwa only registers SWs on web builds
// (gated by NATIVE_BUILD), but an SW registered in an earlier bundled-
// assets session persists in the WebView's origin storage. It then
// intercepts fetches in subsequent dev-server runs and serves stale
// CSS/JS, making live-reload appear broken. Clean the slate on every
// native boot.
if (Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  }).catch(() => { /* best-effort */ });
  // Also clear any Cache Storage entries the SW left behind so the
  // WebView fetches fresh from the dev server / bundled assets.
  if ('caches' in window) {
    caches.keys().then((keys) => {
      keys.forEach((k) => caches.delete(k));
    }).catch(() => { /* best-effort */ });
  }
}

// Kick off RevenueCat configure on native iOS. Fire-and-forget - the
// wrapper is idempotent and the UI awaits it on first use anyway.
void initPurchases();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Native cold start: the launch splash (dark-teal LaunchScreen storyboard) is
// held over the WebView via launchAutoHide:false, so the white WebView
// background never shows while the JS bundle boots. Hide it only AFTER React
// has painted the in-app teal SplashScreen, keeping the handoff teal -> teal
// with no white flash. Double rAF guarantees at least one painted frame; the
// short fade smooths the swap. Both splashes are teal, so exact timing is
// forgiving. Dynamic import keeps the plugin out of the web bundle.
if (Capacitor.isNativePlatform()) {
  void import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          void SplashScreen.hide({ fadeOutDuration: 200 });
        }),
      );
    })
    .catch(() => { /* plugin absent = leave native splash to auto-hide */ });
}
